import { Button, Row, Typography } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { AppContext } from 'context/AppContext';
import { useNotifications } from 'services/NotificationService';
import { NotificationModel, NotificationsResult } from 'services/types/NotificationTypes';
import { NotificationsActionType, notificationsReducer } from './NotificationsReducer';
import NotificationItem from './NotificationItem';

import './Notifications.css';

const { Title } = Typography;

interface NotificationsProps {
  closeHandler?: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({
  closeHandler,
}) => {
  const { t } = useTranslation();
  const { authentication, commonData } = useContext(AppContext);
  const { authKey } = authentication;
  const { userDetails } = commonData || {};
  const [notifications, setNotifications] = useState<NotificationModel[]>([]);

  const [request, dispatchRequest] = useReducer(notificationsReducer, {
    userId: userDetails?.id ?? '',
    userToken: authKey,
    pageSize: 10,
    page: 1,
  });

  const { data, isLoading } = useNotifications(request);

  const { hasMore } = useMemo(() => {
    if (!data) {
      return { hasMore: false };
    } else {
      const result = data as NotificationsResult;
      setNotifications(prevNotifications => [...prevNotifications, ...result.results]);
      return {
        hasMore: result.meta?.pagination ?
          result.meta.pagination.page < result.meta.pagination.pages :
          false
      };
    }
  }, [data]);

  const nextPageHandler = useCallback(() => {
    if (hasMore) {
      dispatchRequest({ type: NotificationsActionType.ChangedPage });
    }
  }, [hasMore, dispatchRequest]);

  const observerRef = useRef(null);

  useEffect(() => {
    let observerRefValue: Element | null = null;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          nextPageHandler();
        }
      },
      { threshold: 1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
      observerRefValue = observerRef.current;
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, [observerRef, nextPageHandler]);

  useEffect(() => {
    setNotifications([]);
    dispatchRequest({
      type: NotificationsActionType.ChangedUser,
      request: {
        userId: userDetails?.id ?? '',
        userToken: authKey,
        pageSize: 10,
        page: 1,
      },
    });
  }, [userDetails, authKey]);

  const emptyView = (
    <div className="notifications-empty-container">
      <Row justify={'center'} className="notifications-empty-text">
        {t('notifications.empty', 'No notifications yet')}
      </Row>
      <Row justify={'center'} className="notifications-empty-button-row">
        <Button
          shape="round"
          className="notifications-okay-button"
          onClick={closeHandler}
        >
          {t('notifications.okay', 'Okay')}
        </Button>
      </Row>
    </div>
  );

  return (
    <>
      {!isLoading && isEmpty(notifications) ? (
        emptyView
      ) : (
        <>
          <Title level={4} className="notifications-title">
            {t('notifications.title', 'Notifications')}
          </Title>

          {/* Removed fixed height and overflow styling to match EarnedMiles */}
          <div>
            {notifications.map((notification, index) => (
              <NotificationItem
                key={`${notification.id}-${index}`}
                notification={notification}
              />
            ))}
          </div>
        </>
      )}

      {isLoading && (
        <div className="notifications-loading-container">
          {[...Array(3)].map((_e, i) => (
            <NotificationItem key={i} />
          ))}
        </div>
      )}

      <div ref={observerRef}></div>
    </>
  );
};

export default Notifications;